// Lead Generation System — the generator's own earnings detail. Per-bucket
// earned / credited / capped with caps remaining, week navigation via ?weekOf=,
// and the recognition-state split (Estimated → Pending → Approved). Every
// figure is a DISPLAY-ONLY estimate; the manager finalizes what's payable and
// pay is handled off-system.

import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  Button,
  Card,
  Skeleton,
  StatusBadge,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@/design-system/components/ui";
import { formatCurrency, formatNumber } from "@/design-system/lib/format";
import { useWidgetData } from "../../dashboard/engine";
import { StatTile, StatTileRow } from "../../dashboard/DashboardPrimitives";
import { leadGeneratorApi, type LeadGenEarningsSummaryDto } from "../../api/leadGenerator";
import { leadGenGradeApi, type LeadGenGradeResult } from "../../api/leadGenGrade";
import { LeadGenGradeBadge } from "./GradeBadge";

/** Cents everywhere on this surface (Part A #1) — 2-dp, never whole dollars. */
function money(amount: number | null | undefined): string {
  return formatCurrency(amount, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** The Monday (UTC midnight) of the week containing `d` — mirrors the backend
 *  pay-week anchor so page-back lands on the same week boundary. */
function mondayOf(d: Date): Date {
  const date = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dow = (date.getUTCDay() + 6) % 7; // 0 = Monday
  date.setUTCDate(date.getUTCDate() - dow);
  return date;
}

function addWeeks(d: Date, weeks: number): Date {
  const next = new Date(d);
  next.setUTCDate(next.getUTCDate() + weeks * 7);
  return next;
}

export function LeadGenEarningsTab() {
  // Offset in weeks from the current pay week (0 = this week, -1 = last week).
  const [offset, setOffset] = useState(0);
  const weekOf = useMemo(() => addWeeks(mondayOf(new Date()), offset), [offset]);
  const weekIso = weekOf.toISOString();

  const earnings = useWidgetData<LeadGenEarningsSummaryDto>(
    async () => leadGeneratorApi.earnings(weekIso),
    [weekIso],
  );
  const summary = earnings.data;

  // The authoritative grade for the SAME week the user is viewing — the badge
  // reads this endpoint, never an FE-computed grade.
  const grade = useWidgetData<LeadGenGradeResult>(
    async () => leadGenGradeApi.grade(weekIso),
    [weekIso],
  );

  return (
    <div className="space-y-space-5">
      {/* Week navigation. */}
      <div className="flex items-center justify-between gap-space-3">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setOffset((o) => o - 1)}
          data-testid="leadgen-earnings-prev"
        >
          <ChevronLeft size={16} aria-hidden /> Previous week
        </Button>
        <p className="text-small font-semibold text-onyx m-0" data-testid="leadgen-earnings-week">
          Week of{" "}
          {(summary?.weekOf ? new Date(summary.weekOf) : weekOf).toLocaleDateString("en-US", {
            timeZone: "UTC",
            month: "short",
            day: "numeric",
            year: "numeric",
          })}
        </p>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setOffset((o) => Math.min(0, o + 1))}
          disabled={offset >= 0}
          data-testid="leadgen-earnings-next"
        >
          Next week <ChevronRight size={16} aria-hidden />
        </Button>
      </div>

      {/* Graphical grade badge (authoritative grade endpoint). Secondary to the
          earnings block — degrades to nothing on its own error. */}
      {grade.status === "loading" && grade.data == null ? (
        <Skeleton className="h-44 w-full" />
      ) : grade.data != null ? (
        <LeadGenGradeBadge grade={grade.data} />
      ) : null}

      {earnings.status === "loading" && summary == null ? (
        <Skeleton className="h-40 w-full" />
      ) : earnings.status === "error" && summary == null ? (
        <Card>
          <p className="text-small text-status-error m-0" role="alert">
            {earnings.error ?? "Couldn't load your earnings."}
          </p>
          <Button variant="text" size="sm" onClick={earnings.reload} className="mt-space-2">
            Try again
          </Button>
        </Card>
      ) : summary != null ? (
        <>
          <StatTileRow>
            <StatTile
              label="Credited"
              value={money(summary.totalCredited)}
              tone="success"
              hint="Counted this week (net of caps)"
            />
            <StatTile label="Earned" value={money(summary.totalEarned)} hint="Before caps" />
            <StatTile
              label="Capped"
              value={money(summary.totalCapped)}
              tone={summary.totalCapped > 0 ? "warning" : "default"}
              hint="Held back by caps"
            />
            <StatTile
              label="Actions"
              value={formatNumber(summary.totalCount)}
              hint="Credited KPI actions"
            />
          </StatTileRow>

          <RecognitionStateCard summary={summary} />

          <BucketTable summary={summary} />

          <p className="text-caption text-gray-mid m-0">
            All figures are a display-only estimate of your KPI activity. Your manager reviews and
            finalizes the payable amount — including any quality grade or multiplier — and pay is
            handled off-system.
          </p>
        </>
      ) : null}
    </div>
  );
}

// ── Recognition state split ──────────────────────────────────────────────────

function RecognitionStateCard({ summary }: { summary: LeadGenEarningsSummaryDto }) {
  return (
    <Card className="space-y-space-3">
      <h3 className="text-small font-semibold uppercase tracking-wide text-gray-mid m-0">
        Where your credits stand
      </h3>
      <div className="grid grid-cols-1 gap-space-3 sm:grid-cols-3">
        <StateLine
          tone="neutral"
          label="Estimated"
          amount={summary.estimatedCredited}
          note="On-click figure, not yet reviewed"
        />
        <StateLine
          tone="warn"
          label="Pending review"
          amount={summary.pendingReviewCredited}
          note="Waiting on the internal team to confirm"
        />
        <StateLine
          tone="ok"
          label="Approved"
          amount={summary.approvedCredited}
          note="Finalized by your manager"
        />
      </div>
      {summary.monthlyTotalCap != null && (
        <p className="text-caption text-gray-mid m-0">
          Monthly cap remaining:{" "}
          <span className="font-semibold text-onyx">
            {money(summary.monthlyTotalCapRemaining ?? 0)}
          </span>{" "}
          of {money(summary.monthlyTotalCap)}.
        </p>
      )}
    </Card>
  );
}

function StateLine({
  tone,
  label,
  amount,
  note,
}: {
  tone: "neutral" | "warn" | "ok";
  label: string;
  amount: number;
  note: string;
}) {
  return (
    <div className="space-y-space-1">
      <div className="flex items-center gap-space-2">
        <StatusBadge tone={tone}>{label}</StatusBadge>
        <span className="text-body font-semibold text-onyx">{money(amount)}</span>
      </div>
      <p className="text-caption text-gray-mid m-0">{note}</p>
    </div>
  );
}

// ── Per-bucket table ─────────────────────────────────────────────────────────

function BucketTable({ summary }: { summary: LeadGenEarningsSummaryDto }) {
  if (summary.buckets.length === 0) {
    return (
      <Card>
        <p className="text-small text-gray-mid m-0">
          No credited activity this week yet. Work the production line to start earning.
        </p>
      </Card>
    );
  }
  return (
    <Card className="overflow-x-auto p-0">
      <Table>
        <THead>
          <TR>
            <TH>Bucket</TH>
            <TH>Count</TH>
            <TH>Earned</TH>
            <TH>Credited</TH>
            <TH>Capped</TH>
            <TH>Weekly cap left</TH>
          </TR>
        </THead>
        <TBody>
          {summary.buckets.map((b) => (
            <TR key={b.bucket}>
              <TD>{b.bucket}</TD>
              <TD>{formatNumber(b.count)}</TD>
              <TD>{money(b.earned)}</TD>
              <TD className="text-status-success font-semibold">{money(b.credited)}</TD>
              <TD className={b.capped > 0 ? "text-status-warning" : undefined}>
                {money(b.capped)}
              </TD>
              <TD>{b.weeklyCapRemaining != null ? money(b.weeklyCapRemaining) : "—"}</TD>
            </TR>
          ))}
        </TBody>
      </Table>
    </Card>
  );
}
